export interface ABNTSection {
  number: string;
  title: string;
  level: 1 | 2 | 3;
  content: string;
  subsections?: ABNTSection[];
}

export interface ManualABNTData {
  metadata: {
    title: string;
    subtitle: string;
    version: string;
    date: string;
    author: string;
    keywords: string[];
    license: string;
    year?: string;
    format?: string;
  };
  preTextual: {
    cover: {
      institution: string;
      title: string;
      subtitle: string;
      version: string;
      date: string;
      badge?: string;
    };
    abstract?: string;
  };
  body: {
    sections: ABNTSection[];
  };
  postTextual?: {
    references?: Array<{
      author: string;
      title: string;
      year: string;
      url?: string;
      accessDate?: string;
    }>;
  };
}

export const fleetoneABNTManual: ManualABNTData = {
  metadata: {
    title: "Manual Completo do FleetONE – Gestão de Frota",
    subtitle: "Guia completo de utilização do sistema FleetONE",
    version: "v1.0",
    date: new Date().toISOString(),
    author: "PortalONE Suite",
    keywords: ["gestão de frotas", "manutenção", "combustível", "pneus", "relatórios", "configurações"],
    license: "© PortalONE Suite",
    year: "2025",
    format: "ABNT"
  },
  
  preTextual: {
    cover: {
      institution: "PortalONE Suite",
      title: "Manual Completo do FleetONE",
      subtitle: "Gestão de Frota",
      version: "v1.0",
      date: "2025",
      badge: "PortalONE Suite"
    },
    abstract: "O FleetONE é o módulo de gestão de frota do PortalONE Suite, desenvolvido para otimizar o controle e operação de frotas de veículos. Este manual apresenta todas as funcionalidades do sistema, desde o cadastro básico até relatórios avançados, oferecendo um guia completo para administradores e usuários do sistema."
  },
  
  body: {
    sections: [
      {
        number: "1",
        title: "INTRODUÇÃO",
        level: 1,
        content: `O FleetONE é o módulo de gestão de frota do PortalONE Suite, desenvolvido para otimizar o controle e a operação de frotas de veículos. Este manual cobre todas as funcionalidades do sistema, desde o cadastro básico até relatórios avançados.`,
        subsections: [
          {
            number: "1.1",
            title: "Objetivos do Sistema",
            level: 2,
            content: `- Centralizar informações de veículos, motoristas e manutenções
- Controlar custos operacionais (combustível, manutenção, pneus)
- Garantir conformidade documental (licenciamento, seguros, CNHs)
- Facilitar o planejamento de manutenções preventivas
- Gerar relatórios gerenciais e operacionais`
          }
        ]
      },
      {
        number: "2",
        title: "DASHBOARD",
        level: 1,
        content: `O Dashboard é a tela inicial do FleetONE e oferece uma visão consolidada da frota.`,
        subsections: [
          {
            number: "2.1",
            title: "Visão Geral",
            level: 2,
            content: `O Dashboard exibe:

- Total de veículos (ativos, em manutenção, inativos)
- Alertas pendentes (vencimentos, manutenções atrasadas)
- Custo mensal (combustível, manutenção, total)
- Quilometragem média da frota
- Gráficos de evolução de custos e consumo

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Dashboard</div>`
          }
        ]
      },
      {
        number: "3",
        title: "VEÍCULOS",
        level: 1,
        content: `O módulo Veículos permite cadastrar e gerenciar toda a frota.`,
        subsections: [
          {
            number: "3.1",
            title: "Cadastrar Novo Veículo",
            level: 2,
            content: `1. Acesse **FleetONE > Veículos**
2. Clique em **+ Novo Veículo**
3. Preencha os dados obrigatórios:
   - Placa
   - Modelo/Marca
   - Ano de fabricação
   - Tipo (carro, van, caminhão, etc.)
   - Centro de custo
4. Dados opcionais:
   - Chassi
   - RENAVAM
   - Cor
   - Capacidade de carga
   - Número de eixos (para pneus)
5. Clique em **Salvar**

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Veículos > + Novo Veículo</div>`
          },
          {
            number: "3.2",
            title: "Editar Veículo",
            level: 2,
            content: `1. Acesse **FleetONE > Veículos**
2. Localize o veículo na lista
3. Clique no ícone de **Editar** (lápis)
4. Altere as informações necessárias
5. Clique em **Salvar**

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Veículos > Editar</div>`
          },
          {
            number: "3.3",
            title: "Inativar Veículo",
            level: 2,
            content: `1. Acesse **FleetONE > Veículos**
2. Clique no veículo desejado
3. Clique em **Inativar**
4. Informe o motivo da inativação
5. Confirme

Veículos inativos não aparecem nas listagens principais, mas mantêm todo o histórico.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Veículos > Inativar</div>`
          }
        ]
      },
      {
        number: "4",
        title: "MOTORISTAS",
        level: 1,
        content: `O módulo Motoristas gerencia o cadastro de condutores e suas atribuições.`,
        subsections: [
          {
            number: "4.1",
            title: "Cadastrar Motorista",
            level: 2,
            content: `1. Acesse **FleetONE > Motoristas**
2. Clique em **+ Novo Motorista**
3. Preencha:
   - Nome completo
   - CPF
   - CNH (número, categoria, validade)
   - Telefone
   - E-mail
4. Clique em **Salvar**

<div class="callout callout--warn">⚠️ <strong>Aviso:</strong> O sistema alerta automaticamente sobre CNHs próximas ao vencimento.</div>

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Motoristas > + Novo Motorista</div>`
          },
          {
            number: "4.2",
            title: "Atribuir Veículo a Motorista",
            level: 2,
            content: `1. Acesse **FleetONE > Motoristas**
2. Clique no motorista
3. Na **aba Veículos Atribuídos**, clique em **+ Atribuir**
4. Selecione o veículo e a data de início
5. Confirme

Um motorista pode ter vários veículos atribuídos em períodos diferentes.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Motoristas > Atribuir Veículo</div>`
          }
        ]
      },
      {
        number: "5",
        title: "MANUTENÇÃO",
        level: 1,
        content: `O módulo de Manutenção controla preventivas e corretivas.`,
        subsections: [
          {
            number: "5.1",
            title: "Planos de Manutenção Preventiva",
            level: 2,
            content: `Planos preventivos podem ser configurados por:

- Quilometragem (ex.: a cada 10.000 km)
- Tempo (ex.: a cada 6 meses)
- Horas de uso (máquinas/equipamentos)

Para criar um plano:

1. Acesse **FleetONE > Manutenção > Planos Preventivos**
2. Clique em **+ Novo Plano**
3. Defina:
   - Nome do plano (ex.: "Troca de óleo")
   - Periodicidade (km, tempo ou horas)
   - Veículos aplicáveis
   - Checklist de itens
4. **Salve**

<div class="callout callout--ok">✅ <strong>Dica:</strong> O sistema gerará alertas automaticamente.</div>

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Manutenção > Planos Preventivos</div>`
          },
          {
            number: "5.2",
            title: "Registrar Ordem de Serviço",
            level: 2,
            content: `1. Acesse **FleetONE > Manutenção > Ordens de Serviço**
2. Clique em **+ Nova OS**
3. Preencha:
   - Veículo
   - Tipo (preventiva/corretiva)
   - Descrição do serviço
   - Oficina/mecânico
   - Data de entrada e saída
   - Quilometragem no momento
   - Custo de peças e mão de obra
   - Anexe notas fiscais (opcional)
4. **Salve**

<div class="callout callout--ok">✅ <strong>Registro:</strong> A OS ficará disponível no histórico do veículo.</div>

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Manutenção > Nova OS</div>`
          },
          {
            number: "5.3",
            title: "Histórico de Manutenções",
            level: 2,
            content: `O histórico completo pode ser acessado:

- Por veículo: **FleetONE > Veículos > [Veículo] > Aba Manutenção**
- Geral: **FleetONE > Manutenção > Histórico**

Filtros disponíveis: período, tipo, oficina, status.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Manutenção > Histórico</div>`
          }
        ]
      },
      {
        number: "6",
        title: "COMBUSTÍVEL",
        level: 1,
        content: `O módulo de Combustível registra abastecimentos e analisa consumo.`,
        subsections: [
          {
            number: "6.1",
            title: "Registrar Abastecimento",
            level: 2,
            content: `1. Acesse **FleetONE > Combustível**
2. Clique em **+ Novo Abastecimento**
3. Preencha:
   - Veículo
   - Data e hora
   - Quilometragem atual
   - Litros abastecidos
   - Valor total
   - Tipo de combustível
   - Posto (opcional)
   - Anexe nota fiscal (opcional)
4. **Salve**

O sistema calcula automaticamente:
- Consumo médio (km/l)
- Custo por km

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Combustível > Novo Abastecimento</div>`
          },
          {
            number: "6.2",
            title: "Análise de Consumo",
            level: 2,
            content: `Análises disponíveis:

- Consumo médio por veículo
- Evolução do consumo (gráfico mensal)
- Comparativo de veículos
- Custo total por período
- Identificação de anomalias (consumo fora do padrão)

Acesse: **FleetONE > Combustível > Análises**

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Combustível > Análises</div>`
          }
        ]
      },
      {
        number: "7",
        title: "GESTÃO DE PNEUS",
        level: 1,
        content: `O módulo de Pneus gerencia todo o ciclo de vida: cadastro, instalação, rodízio, recapagem e descarte.`,
        subsections: [
          {
            number: "7.1",
            title: "Cadastro de Pneus",
            level: 2,
            content: `Cada pneu recebe um código único (número de série ou código interno).`,
            subsections: [
              {
                number: "7.1.1",
                title: "Como Cadastrar um Novo Pneu",
                level: 3,
                content: `1. Acesse **FleetONE > Pneus**
2. Clique em **+ Novo Pneu**
3. Preencha:
   - Código/Série do pneu
   - Marca
   - Modelo
   - Medida (ex.: 275/80R22.5)
   - DOT (data de fabricação)
   - Sulco inicial (mm)
   - Custo de aquisição
4. **Salve**

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Pneus > Novo Pneu</div>`
              },
              {
                number: "7.1.2",
                title: "Status dos Pneus",
                level: 3,
                content: `- **Estoque:** pneu disponível, não instalado
- **Em uso:** instalado em veículo
- **Recapagem:** em processo de recapagem
- **Reparo:** em conserto
- **Descartado:** fim de vida útil`
              }
            ]
          },
          {
            number: "7.2",
            title: "Instalação de Pneus",
            level: 2,
            content: `Ao instalar um pneu, registre a posição exata no veículo.`,
            subsections: [
              {
                number: "7.2.1",
                title: "Instalar Pneu em Veículo",
                level: 3,
                content: `1. Acesse **FleetONE > Veículos > [Veículo] > Pneus**
2. Clique em **Instalar Pneu**
3. Selecione o pneu (do estoque)
4. Escolha a posição:
   - Dianteiro Esquerdo (DE)
   - Dianteiro Direito (DD)
   - Traseiro Esquerdo Interno/Externo
   - Traseiro Direito Interno/Externo
   - Estepe
5. Informe a quilometragem da instalação
6. **Salve**

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Veículos > Pneus > Instalar</div>`
              }
            ]
          },
          {
            number: "7.3",
            title: "Rodízio de Pneus",
            level: 2,
            content: `O rodízio equilibra o desgaste e prolonga a vida útil.`,
            subsections: [
              {
                number: "7.3.1",
                title: "Realizar Rodízio",
                level: 3,
                content: `1. Acesse **FleetONE > Veículos > [Veículo] > Pneus**
2. Clique em **Realizar Rodízio**
3. O sistema mostra as posições atuais
4. Defina as novas posições seguindo o padrão recomendado
5. Informe a quilometragem do rodízio
6. **Salve**

O histórico de movimentações fica registrado.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Veículos > Pneus > Rodízio</div>`
              },
              {
                number: "7.3.2",
                title: "Recomendações de Rodízio",
                level: 3,
                content: `- Veículos leves: a cada 10.000 km
- Caminhões: a cada 20.000 km ou conforme desgaste
- Sempre registre medições de sulco antes e depois`
              }
            ]
          },
          {
            number: "7.4",
            title: "Manutenção e Reparos",
            level: 2,
            content: `Pneus podem ser recapados ou reparados para prolongar sua vida útil.`,
            subsections: [
              {
                number: "7.4.1",
                title: "Registrar Recapagem",
                level: 3,
                content: `1. Acesse **FleetONE > Pneus > [Pneu]**
2. Clique em **Enviar para Recapagem**
3. Preencha:
   - Data de envio
   - Empresa recapadora
   - Sulco antes da recapagem
   - Custo estimado
4. **Salve**

O pneu fica com status "Recapagem" até o retorno.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Pneus > Recapagem</div>`
              },
              {
                number: "7.4.2",
                title: "Registrar Retorno da Recapagem",
                level: 3,
                content: `1. Acesse **FleetONE > Pneus > [Pneu]**
2. Clique em **Retorno de Recapagem**
3. Preencha:
   - Data de retorno
   - Sulco após recapagem
   - Custo real
4. **Salve**

O pneu volta ao status "Estoque".

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Pneus > Retorno Recapagem</div>`
              },
              {
                number: "7.4.3",
                title: "Registrar Reparo (Furo/Corte)",
                level: 3,
                content: `1. Acesse **FleetONE > Pneus > [Pneu]**
2. Clique em **Registrar Reparo**
3. Preencha:
   - Data
   - Tipo de dano
   - Custo
   - Observações
4. **Salve**

Reparos ficam no histórico do pneu.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Pneus > Reparo</div>`
              }
            ]
          },
          {
            number: "7.5",
            title: "Medições e Inspeções",
            level: 2,
            content: `Medições periódicas são essenciais para segurança e controle de custos.`,
            subsections: [
              {
                number: "7.5.1",
                title: "Medir Profundidade dos Sulcos",
                level: 3,
                content: `1. Acesse **FleetONE > Veículos > [Veículo] > Pneus**
2. Clique em **Medir Sulcos**
3. Informe a profundidade de cada pneu (em mm)
4. Informe a quilometragem
5. **Salve**

O sistema alerta automaticamente quando o sulco atingir o mínimo configurado (ex.: 3 mm).

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Veículos > Pneus > Medir Sulcos</div>`
              },
              {
                number: "7.5.2",
                title: "Verificar Pressão",
                level: 3,
                content: `1. Acesse **FleetONE > Veículos > [Veículo] > Pneus**
2. Clique em **Verificar Pressão**
3. Informe a pressão medida (PSI ou bar)
4. Sistema compara com pressão ideal configurada
5. **Salve**

<div class="callout callout--warn">⚠️ <strong>Aviso:</strong> Pressão incorreta aumenta o consumo e acelera o desgaste.</div>

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Veículos > Pneus > Pressão</div>`
              }
            ]
          },
          {
            number: "7.6",
            title: "Descarte de Pneus",
            level: 2,
            content: `Quando um pneu atinge o fim da vida útil, registre o descarte.`,
            subsections: [
              {
                number: "7.6.1",
                title: "Descartar Pneu",
                level: 3,
                content: `1. Acesse **FleetONE > Pneus > [Pneu]**
2. Clique em **Descartar**
3. Informe:
   - Motivo (desgaste, dano irreparável, etc.)
   - Data
   - Quilometragem percorrida
4. **Confirme**

O pneu fica com status "Descartado" e não aparece mais nas listagens ativas.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Pneus > Descartar</div>`
              }
            ]
          },
          {
            number: "7.7",
            title: "Relatórios de Pneus",
            level: 2,
            content: `O FleetONE oferece diversos relatórios sobre pneus.`,
            subsections: [
              {
                number: "7.7.1",
                title: "Relatórios Disponíveis",
                level: 3,
                content: `- **Inventário de Pneus:** status atual de todos os pneus
- **Histórico de Movimentações:** rodízios, instalações, remoções
- **Custo por Pneu:** aquisição + recapagens + reparos
- **Custo por Quilômetro:** custo total / km rodados
- **Performance:** pneus com maior/menor durabilidade
- **Recapagens:** histórico e custos
- **Alertas:** pneus próximos ao descarte`
              },
              {
                number: "7.7.2",
                title: "Exportação de Dados",
                level: 3,
                content: `Todos os relatórios podem ser exportados em PDF, Excel ou CSV.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Pneus > Relatórios</div>`
              }
            ]
          },
          {
            number: "7.8",
            title: "Alertas Automáticos de Pneus",
            level: 2,
            content: `O sistema emite alertas para:

- Sulco mínimo atingido (configurável, padrão: 3 mm)
- Rodízio necessário (baseado em km ou tempo)
- Inspeção atrasada (baseado em periodicidade configurada)
- Pressão incorreta (fora da faixa ideal)
- Número máximo de recapagens (ex.: 3 recapagens)

Configure os alertas em **FleetONE > Configurações > Pneus**.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Configurações > Pneus > Alertas</div>`
          },
          {
            number: "7.9",
            title: "Melhores Práticas",
            level: 2,
            content: `<div class="callout callout--ok">
✅ <strong>Boas práticas para gestão de pneus:</strong>

- Cadastre todos os pneus com código único
- Registre sempre a posição ao instalar
- Faça medições de sulco periodicamente (sugestão: 30 dias)
- Realize rodízios conforme recomendação do fabricante
- Verifique pressão semanalmente
- Envie para recapagem antes do sulco mínimo (ideal: 5 mm)
- Registre todos os custos (reparos, recapagens)
- Use os relatórios para identificar pneus problemáticos
- Configure alertas para não perder prazos
</div>`
          }
        ]
      },
      {
        number: "8",
        title: "DOCUMENTOS",
        level: 1,
        content: `O módulo de Documentos centraliza toda a documentação da frota e motoristas.`,
        subsections: [
          {
            number: "8.1",
            title: "Tipos de Documentos",
            level: 2,
            content: `Documentos gerenciados:

- **Veículos:**
  - Licenciamento (CRLV)
  - Seguro
  - IPVA
  - Inspeção veicular
  - Contrato de aluguel/financiamento
- **Motoristas:**
  - CNH
  - Exame toxicológico
  - Exame médico
  - Certidões

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Documentos</div>`
          },
          {
            number: "8.2",
            title: "Upload de Documentos",
            level: 2,
            content: `1. Acesse o veículo ou motorista
2. Vá para a **aba Documentos**
3. Clique em **+ Novo Documento**
4. Selecione o tipo
5. Informe a data de validade
6. Faça upload do arquivo (PDF, imagem)
7. **Salve**

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Veículos/Motoristas > Documentos</div>`
          },
          {
            number: "8.3",
            title: "Alertas de Vencimento",
            level: 2,
            content: `O sistema alerta automaticamente sobre documentos próximos ao vencimento.

Defina com quantos dias de antecedência deseja receber alertas em **Configurações > Alertas**.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Configurações > Alertas</div>`
          }
        ]
      },
      {
        number: "9",
        title: "ALERTAS",
        level: 1,
        content: `O módulo de Alertas centraliza notificações importantes.`,
        subsections: [
          {
            number: "9.1",
            title: "Tipos de Alertas",
            level: 2,
            content: `- Documentos vencendo: CRLV, CNH, seguros, etc.
- Manutenções preventivas: próximas ou atrasadas
- Pneus: sulco mínimo, rodízio necessário
- Custos elevados: consumo acima do padrão
- Quilometragem: marcos importantes (ex.: 100.000 km)

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Alertas</div>`
          },
          {
            number: "9.2",
            title: "Configurar Alertas",
            level: 2,
            content: `1. Acesse **FleetONE > Configurações > Alertas**
2. Defina:
   - Dias de antecedência para vencimentos
   - Tolerância para atrasos
   - Responsáveis por cada tipo de alerta
   - Canais de notificação (e-mail, sistema, WhatsApp)
3. **Salve**

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Configurações > Alertas</div>`
          }
        ]
      },
      {
        number: "10",
        title: "RELATÓRIOS",
        level: 1,
        content: `O módulo de Relatórios oferece análises completas da frota.`,
        subsections: [
          {
            number: "10.1",
            title: "Relatórios Disponíveis",
            level: 2,
            content: `- **Dashboard Executivo:** visão geral da frota
- **Relatório de Veículos:** dados completos dos veículos
- **Relatório de Motoristas:** informações e atribuições
- **Relatório de Manutenção:** histórico e custos
- **Relatório de Combustível:** consumo e análises
- **Relatório de Pneus:** inventário e performance
- **Relatório de Custos:** análise financeira completa
- **Relatório Operacional:** indicadores de desempenho

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Relatórios</div>`
          },
          {
            number: "10.2",
            title: "Filtros e Período",
            level: 2,
            content: `Todos os relatórios permitem filtrar por:

- Período (data inicial e final)
- Veículo específico ou grupo
- Centro de custo
- Status

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Relatórios > Filtros</div>`
          },
          {
            number: "10.3",
            title: "Exportação",
            level: 2,
            content: `Todos os relatórios podem ser exportados em PDF, Excel ou CSV.

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Relatórios > Exportar</div>`
          }
        ]
      },
      {
        number: "11",
        title: "CONFIGURAÇÕES",
        level: 1,
        content: `O módulo de Configurações permite personalizar o sistema.`,
        subsections: [
          {
            number: "11.1",
            title: "Configurações de Alertas",
            level: 2,
            content: `Defina com quantos dias de antecedência deseja ser alertado sobre:

- Vencimento de documentos (padrão: 30 dias)
- Vencimento de CNH (padrão: 60 dias)
- Manutenções preventivas (padrão: 7 dias)

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Configurações > Alertas</div>`
          },
          {
            number: "11.2",
            title: "Configurações de Manutenção",
            level: 2,
            content: `Configure periodicidades padrão para manutenções preventivas:

- Troca de óleo
- Revisão geral
- Alinhamento e balanceamento
- Filtros (ar, combustível, cabine)

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Configurações > Manutenção</div>`
          },
          {
            number: "11.3",
            title: "Configurações de Pneus",
            level: 2,
            content: `Configure parâmetros para gestão de pneus:

- Quilometragem padrão para rodízio (ex.: 10.000 km)
- Profundidade mínima de alerta (ex.: 3 mm)
- Número máximo de recapagens permitidas (ex.: 3)
- Pressão padrão por tipo de veículo
- Intervalo de inspeção (ex.: 30 dias)

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Configurações > Pneus</div>`
          },
          {
            number: "11.4",
            title: "Configurações Gerais",
            level: 2,
            content: `Outras configurações:

- Fuso horário
- Formato de data
- Moeda
- Unidades de medida

<div class="callout"><strong>Nota — Acesso:</strong> FleetONE > Configurações > Gerais</div>`
          }
        ]
      },
      {
        number: "12",
        title: "REFERÊNCIAS",
        level: 1,
        content: `- ASSOCIAÇÃO BRASILEIRA DE NORMAS TÉCNICAS. NBR 14724: Informação e documentação — Trabalhos acadêmicos — Apresentação. 2011.
- ASSOCIAÇÃO BRASILEIRA DE NORMAS TÉCNICAS. NBR 6024: Informação e documentação — Numeração progressiva das seções de um documento. 2012.
- ASSOCIAÇÃO BRASILEIRA DE NORMAS TÉCNICAS. NBR 6027: Informação e documentação — Sumário — Apresentação. 2012.
- PORTALONE SUITE. FleetONE — Sistema de Gestão de Frotas. 2025. Disponível em: [portalonesuite.com.br](https://www.portalonesuite.com.br). Acesso em: 18 out. 2025.`
      }
    ]
  },
  
  postTextual: {
    references: [
      {
        author: "ASSOCIAÇÃO BRASILEIRA DE NORMAS TÉCNICAS",
        title: "NBR 14724: Informação e documentação — Trabalhos acadêmicos — Apresentação",
        year: "2011"
      },
      {
        author: "ASSOCIAÇÃO BRASILEIRA DE NORMAS TÉCNICAS",
        title: "NBR 6024: Informação e documentação — Numeração progressiva das seções de um documento",
        year: "2012"
      },
      {
        author: "ASSOCIAÇÃO BRASILEIRA DE NORMAS TÉCNICAS",
        title: "NBR 6027: Informação e documentação — Sumário — Apresentação",
        year: "2012"
      },
      {
        author: "PORTALONE SUITE",
        title: "FleetONE — Sistema de Gestão de Frotas",
        year: "2025",
        url: "https://www.portalonesuite.com.br",
        accessDate: "18 out. 2025"
      }
    ]
  }
};
